import { useState } from 'react';
import { History } from 'lucide-react';
import { useAuditTrail } from '@my/directory-api/hooks';
import { cn, HistoryDialog } from '@my/ui';

interface PartyHistoryButtonProps {
  partyId: string | null;
  organisationId: string | null;
  /** Shown in the dialog title so the reader knows whose history this is. */
  partyName: string | null;
}

function entityLabel(entityType: string): string {
  switch (entityType) {
    case 'Party':
      return 'Customer / supplier';
    case 'PartyContact':
      return 'Contact';
    case 'PartyDocument':
      return 'Document';
    case 'Address':
      return 'Address';
    default:
      return entityType;
  }
}

/**
 * Labels for the columns whose names do not survive contact with an operator.
 * Everything else is left alone: renaming every field would be a second
 * vocabulary to maintain, and most of them already read correctly.
 */
const FIELD_LABELS: Record<string, string> = {
  PaymentTermDays: 'Payment terms (days)',
  PaymentTermBasis: 'Payment terms counted from',
  PaymentMethod: 'Payment method',
  BillingFrequency: 'Billing frequency',
  CreditLimit: 'Credit limit',
  TaxSchemeCountryCode: 'Tax country',
  EuVatStatement: 'EU VAT statement',
  CurrencyIsoCode: 'Currency',
  PreferredDocumentLayoutsJson: 'Document layouts',
  TmsRequiredOrderReferencesJson: 'Required references',
  MaxConcurrentAssignments: 'Assignment cap',
  AccountOwnerId: 'Account owner',
};

/**
 * This customer's history — its own changes plus its contacts', documents' and
 * addresses', since all three file under the party.
 *
 * <p>
 * Reads Directory's trail, not TMS's: the party lives there, and so does the
 * record of who changed it. Same button-and-dialog shape as the order screen,
 * so the feature reads the same wherever it appears.
 * </p>
 */
export function PartyHistoryButton({
  partyId,
  organisationId,
  partyName,
}: PartyHistoryButtonProps) {
  const [open, setOpen] = useState(false);
  const query = useAuditTrail(organisationId, 'Party', partyId, { enabled: open });

  // Nothing to show for a party that has not been created yet.
  if (!partyId) return null;

  return (
    <>
      <button
        type="button"
        aria-label="History"
        title="Who changed what, and when"
        onClick={() => setOpen(true)}
        className={cn(
          'flex h-7 w-7 items-center justify-center rounded-sm',
          'text-slate-400 hover:bg-slate-100 hover:text-slate-700',
        )}
      >
        <History className="h-3.5 w-3.5" />
      </button>

      <HistoryDialog
        open={open}
        onClose={() => setOpen(false)}
        title={`History${partyName ? ` · ${partyName}` : ''}`}
        subtitle="who changed what, and when"
        entries={query.data ?? []}
        isLoading={query.isPending}
        error={query.isError ? 'Could not load this history.' : null}
        hasMore={query.hasNextPage}
        isLoadingMore={query.isFetchingNextPage}
        onLoadMore={() => void query.fetchNextPage()}
        entityLabel={entityLabel}
        fieldLabel={(_entityType, field) => FIELD_LABELS[field] ?? field}
        emptyMessage="Nothing recorded yet — the history starts from when auditing was switched on."
      />
    </>
  );
}
